import {
  blackColor,
  dangerColor,
  grayColor,
  hexToRgb,
  successColor,
  whiteColor,
} from "../../../assets/jss/material-dashboard-pro-react";

// Shared styles for Alignment Session components
const alignmentSessionStyles = {
  // Navigation styles
  navigationWrapper: {
    position: "relative",
    zIndex: 20,
    marginBottom: "-20px",
  },
  pageContentWrapper: {
    transition: "opacity 0.2s ease-in-out",
    opacity: 1,
  },
  pageContentFading: {
    opacity: 0,
  },
  // Focus Mode Overlay styles
  focusModeOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#eeeeee", // Same as app background
    zIndex: 1200, // Above navbar
    opacity: 0,
    visibility: "hidden",
    transition: "opacity 0.3s ease-in-out, visibility 0.3s ease-in-out",
  },
  focusModeOverlayActive: {
    opacity: 1,
    visibility: "visible",
  },
  focusModeContent: {
    position: "relative",
    padding: "8px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  // Focus mode specific wrapper for page content (grows to fill space)
  focusModePageWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    // Override section heights to grow in focus mode
    "& > div": {
      // Target section containers
      flex: 1,
      minHeight: "unset !important",
      height: "100% !important",
    },
  },
  // Mirrored footer for focus mode
  focusModeFooter: {
    marginBottom: "-7px",
    padding: "4px 8px 0 0",
    fontSize: "14px",
    color: grayColor[0],
    textAlign: "right",
    "& a": {
      color: grayColor[2],
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
  pillsContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pillsWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px",
    backgroundColor: grayColor[10],
    borderRadius: "20px",
  },
  pillsBackground: {
    position: "absolute",
    top: "4px",
    height: "calc(100% - 8px)",
    borderRadius: "16px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
    zIndex: 1,
  },
  pillItem: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "32px",
    padding: "0 18px",
    borderRadius: "16px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    textAlign: "center",
    userSelect: "none",
    zIndex: 2,
    // "&:hover": {
    //   transform: "scale(1.02)",
    // },
  },
  pillItemInactive: {
    color: grayColor[2],
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.06)",
    },
  },
  pillItemActive: {
    color: whiteColor,
    fontWeight: "500",
    // "&:hover": {
    //   transform: "scale(1.02)",
    // },
  },
  pillText: {
    position: "relative",
    zIndex: 2,
    whiteSpace: "nowrap",
    fontSize: "14px",
  },
  dataUpdateText: {
    position: "fixed",
    bottom: "1px",
    left: "110px",
    // transform: "translateX(-50%)",
    textAlign: "center",
    fontSize: "12px",
    fontWeight: "300",
    color: grayColor[6],
    margin: "0",
    zIndex: 10,
    "& strong": {
      fontWeight: "600",
    },
  },
  dataUpdateTextFocusMode: {
    position: "absolute",
    bottom: "1px",
    left: "16px",
    // transform: "translateX(-50%)",
    textAlign: "center",
    fontSize: "12px",
    fontWeight: "300",
    color: grayColor[6],
    margin: "0",
    "& strong": {
      fontWeight: "600",
    },
  },

  // Navigation buttons styles
  navButtonsContainer: {
    position: "absolute",
    right: "0",
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  // Left toggle container (for highlight switch)
  navLeftContainer: {
    position: "absolute",
    left: "0",
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: "10px",
    marginLeft: "10px",
  },
  // Toggle switch styles
  toggleSwitch: {
    position: "relative",
    display: "inline-block",
    width: "44px",
    height: "24px",
    cursor: "pointer",
  },
  toggleSwitchInput: {
    opacity: 0,
    width: 0,
    height: 0,
  },
  toggleSwitchSlider: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: grayColor[5],
    borderRadius: "24px",
    transition: "background-color 0.3s ease",
    "&::before": {
      position: "absolute",
      content: '""',
      height: "18px",
      width: "18px",
      left: "3px",
      bottom: "3px",
      backgroundColor: whiteColor,
      borderRadius: "50%",
      transition: "transform 0.3s ease",
      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    },
  },
  toggleSwitchSliderChecked: {
    backgroundColor: grayColor[2],
    "&::before": {
      transform: "translateX(20px)",
    },
  },
  toggleSwitchLabel: {
    fontSize: "13px",
    fontWeight: "400",
    color: grayColor[2],
    userSelect: "none",
    whiteSpace: "nowrap",
  },
  toggleSwitchWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  comparisonLabel: {
    fontSize: "11px",
    color: grayColor[1],
    fontStyle: "italic",
    whiteSpace: "nowrap",
    marginLeft: "12px",
    userSelect: "none",
  },
  navButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    backgroundColor: whiteColor,
    border: "1px solid " + grayColor[5],
    cursor: "pointer",
    transition: "all 0.2s ease",
    color: grayColor[2],
    "&:hover": {
      backgroundColor: grayColor[10],
      borderColor: grayColor[4],
      transform: "scale(1.1)",
    },
    "& svg": {
      fontSize: "24px",
    },
  },
  navButtonDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
    "&:hover": {
      backgroundColor: whiteColor,
      borderColor: grayColor[5],
      transform: "none",
    },
  },

  // Period reference button styles
  periodReferenceContainer: {
    position: "relative",
    marginRight: "8px",
  },
  periodReferenceButton: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 12px",
    borderRadius: "18px",
    backgroundColor: whiteColor,
    border: "1px solid " + grayColor[5],
    cursor: "pointer",
    transition: "all 0.2s ease",
    color: grayColor[2],
    fontSize: "12px",
    fontWeight: "500",
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: grayColor[10],
      borderColor: grayColor[4],
    },
  },
  periodReferenceMenu: {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: "4px",
    backgroundColor: whiteColor,
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    border: "1px solid " + grayColor[8],
    overflow: "hidden",
    zIndex: 1000,
    minWidth: "160px",
  },
  periodReferenceMenuItem: {
    padding: "10px 16px",
    fontSize: "12px",
    color: grayColor[2],
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: grayColor[10],
    },
  },
  periodReferenceMenuItemActive: {
    fontWeight: "600",
  },
  periodReferenceMenuItemDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },

  // Card styles
  mainCard: {
    marginTop: "0",
    minHeight: "calc(100vh - 150px)",
    display: "flex",
    flexDirection: "column",
  },
  cardContent: {
    padding: "40px 20px",
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    color: grayColor[0],
  },
  contentTitle: {
    fontSize: "24px",
    fontWeight: "500",
    marginBottom: "10px",
  },
  contentSubtitle: {
    fontSize: "16px",
    color: grayColor[6],
  },
  singlePageCard: {
    minHeight: "calc(100vh - 170px)",
    display: "flex",
    flexDirection: "column",
    marginTop: "30px",
  },

  // Overview page styles
  overviewContainer: {
    display: "flex",
    width: "100%",
    gap: "10px",
    marginTop: "30px",
  },
  overviewCard: {
    flex: 1,
    minHeight: "calc(100vh - 175px)",
    display: "flex",
    flexDirection: "column",
    margin: 0,
    overflow: "visible",
  },
  overviewCardBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "0",
    overflow: "visible",
  },
  overviewCardBodyPnl: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "0 !important",
  },

  // By Dimension page styles
  byDimensionContainer: {
    marginTop: "30px",
    display: "flex",
    gap: "10px",
    height: "calc(100vh - 176px)",
  },
  byDimensionCard: {
    flex: 1,
    marginBottom: 0,
    marginTop: 0,
    maxHeight: "100%",
    display: "flex",
    flexDirection: "column",
  },
  byDimensionCardBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "12px 0",
    minHeight: 0,
    overflow: "hidden",
  },
  byDimensionHeader: {
    height: "42px",
    display: "flex",
    alignItems: "center",
    marginBottom: "12px",
  },
  byDimensionSelect: {
    flex: 1,
  },

  // Dimension table styles
  dimensionTable: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  dimensionTableWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minHeight: 0,
  },
  dimensionTableHeader: {
    display: "grid",
    gridTemplateColumns: "32% repeat(5, 1fr)",
    gap: "2px",
    paddingBottom: "8px",
    borderBottom: "1px solid " + grayColor[8],
    flexShrink: 0,
    backgroundColor: whiteColor,
    alignItems: "center",
  },
  dimensionTableHeaderCell: {
    fontSize: "12px",
    fontWeight: "500",
    color: grayColor[2],
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    // gap: "2px",
    "&:first-child": {
      textAlign: "left",
      alignItems: "flex-start",
    },
  },
  dimensionTableHeaderUnit: {
    fontSize: "11px",
    fontWeight: "400",
    color: grayColor[3],
    lineHeight: "1.1",
  },
  dimensionTableBody: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    paddingTop: "8px",
    // Overlay scrollbar - doesn't take space
    "@supports (overflow: overlay)": {
      overflowY: "overlay",
    },
    scrollbarWidth: "thin",
    scrollbarColor: "transparent transparent",
    "&:hover": {
      scrollbarColor: grayColor[6] + " transparent",
    },
    // Webkit browsers
    "&::-webkit-scrollbar": {
      width: "6px",
      position: "absolute",
      right: 0,
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "transparent",
      borderRadius: "3px",
      transition: "background 0.2s ease",
    },
    "&:hover::-webkit-scrollbar-thumb": {
      background: grayColor[6],
    },
  },
  dimensionTableRow: {
    display: "grid",
    gridTemplateColumns: "32% repeat(5, 1fr)",
    gap: "2px",
    padding: "6px 4px",
    margin: "0 -4px",
    borderBottom: "1px solid " + grayColor[9],
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.05)",
    },
    "&:last-child": {
      borderBottom: "none",
    },
  },
  dimensionTableRowTotal: {
    fontWeight: "400",
    backgroundColor: grayColor[10],
    margin: "0 -16px",
    padding: "6px 16px",
  },
  dimensionTableCell: {
    fontSize: "14px",
    color: grayColor[1],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 0,
    "&:first-child": {
      justifyContent: "flex-start",
      overflow: "hidden",
    },
  },
  dimensionElementName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    display: "block",
    cursor: "default",
    paddingLeft: "6px",
  },
  dimensionValueWithBadge: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "1px",
    lineHeight: 1.2,
  },
  dimensionColoredValue: {
    fontWeight: "400",
    fontSize: "14px",
    transition: "opacity 0.3s ease, transform 0.3s ease",
    display: "inline-block", // Required for transform to work on inline elements
  },
  dimensionBadge: {
    width: "20px",
    height: "3px",
    borderRadius: "2px",
    "&.n1": { backgroundColor: "#F44336" },
    "&.n2": { backgroundColor: "#F76C24" },
    "&.n3": { backgroundColor: "#FB9612" },
    "&.n4": { backgroundColor: "#FFC000" },
    "&.n5": { backgroundColor: "#F6C737" },
    "&.n6": { backgroundColor: "#EECE6E" },
    "&.n7": { backgroundColor: "#E5D5A5" },
    "&.n8": { backgroundColor: "#DDDDDD" },
    "&.n9": { backgroundColor: "#C8D6C8" },
    "&.n10": { backgroundColor: "#B3CFB4" },
    "&.n11": { backgroundColor: "#9EC9A0" },
    "&.n12": { backgroundColor: "#8AC28C" },
    "&.n13": { backgroundColor: "#75BC78" },
    "&.n14": { backgroundColor: "#60B564" },
    "&.n15": { backgroundColor: "#4CAF50" },
  },
  dimensionHeatmapSection: {
    marginTop: "auto",
    paddingTop: "12px",
    borderTop: "1px solid " + grayColor[8],
  },
  dimensionHeatmapTitle: {
    fontSize: "11px",
    fontWeight: "500",
    color: grayColor[4],
    marginBottom: "8px",
  },
  dimensionChartSection: {
    marginTop: "auto",
    padding: "0 8px",
    // borderTop: "1px solid " + grayColor[8],
    height: "200px", // Fixed height to ensure alignment across blocks
    display: "flex",
    flexDirection: "column",
  },
  dimensionChartTitle: {
    fontSize: "12px",
    fontWeight: "400",
    color: "rgb(120, 120, 120)",
    backgroundColor: "rgba(238, 238, 238, 0.6)",
    textAlign: "center",
    marginTop: "6px",
    flexShrink: 0,
    marginLeft: "-8px",
    marginRight: "-8px",
  },
  dimensionChartWrapper: {
    height: "110px",
    marginBottom: "12px",
    flexShrink: 0,
  },
  dimensionChartLegend: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px 12px",
    justifyContent: "center",
  },
  dimensionChartLegendItem: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  dimensionChartLegendColor: {
    width: "12px",
    height: "12px",
    borderRadius: "2px",
    flexShrink: 0,
  },
  dimensionChartLegendLabel: {
    fontSize: "12px",
    color: grayColor[3],
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "80px",
  },

  // Highlights page styles
  highlightsContainer: {
    marginTop: "30px",
  },
  highlightsContainerFocus: {
    marginTop: "30px",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  highlightsMainLayout: {
    display: "flex",
    gap: "10px",
    minHeight: "calc(100vh - 176px)",
  },
  highlightsMainLayoutFocus: {
    display: "flex",
    gap: "10px",
    flex: 1,
    maxHeight: "100%",
    overflow: "hidden",
    // Ensure all direct child cards fill the height
    "& > div": {
      maxHeight: "100%",
      height: "100%",
    },
  },
  indicatorsColumnCard: {
    flex: "0 0 12%",
    marginBottom: 0,
    marginTop: 0,
    display: "flex",
    flexDirection: "column",
    maxHeight: "calc(100vh - 176px)",
    overflow: "hidden",
  },
  indicatorsColumnBody: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    padding: "8px 8px",
    overflow: "hidden",
  },
  indicatorsVerticalList: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
    flex: 1,
    overflow: "hidden",
  },
  indicatorVerticalWrapper: {
    textAlign: "center",
    padding: "4px 0",
    flex: "1 1 0",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minHeight: 0,
  },
  indicatorsCard: {
    marginBottom: "10px",
    marginTop: 0,
  },
  indicatorsCardBody: {
    padding: "8px 4px",
  },
  indicatorsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "20px",
  },
  indicatorsGrid5: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "16px",
  },
  indicatorWrapper: {
    textAlign: "center",
  },
  indicatorLabel: {
    fontSize: "11px",
    color: "#999",
    marginBottom: "2px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  indicatorValue: {
    marginTop: "4px",
    fontSize: "26px",
    fontWeight: "300",
    lineHeight: 1.1,
  },
  indicatorValuePositive: {
    color: grayColor[1],
  },
  indicatorValueNegative: {
    color: "#f44336",
  },
  indicatorSubtext: {
    marginTop: "4px",
    fontSize: "14px",
    lineHeight: 1.2,
  },
  indicatorSubtextPositive: {
    color: grayColor[2],
  },
  indicatorSubtextNegative: {
    color: "#f44336",
  },
  highlightsContent: {
    flex: 1,
    display: "flex",
    gap: "10px",
  },
  highlightsLeftColumn: {
    flex: 1,
    margin: 0,
    display: "flex",
    flexDirection: "column",
  },
  plCardsRow: {
    display: "flex",
    gap: "10px",
    flex: 1,
    minWidth: 0,
    width: "100%",
  },
  plCardHighlights: {
    flex: 1,
    marginBottom: 0,
    marginTop: 0,
    borderRadius: "6px",
    "&:first-child": {
      flex: 1.5,
    },
    "&:last-child": {
      flex: 0.8,
    },
  },
  plCardClient: {
    flex: 1,
    minWidth: 0,
    marginBottom: 0,
    marginTop: 0,
    borderRadius: "4px",
    boxShadow: "none",
    overflow: "hidden",
    "&:first-child": {
      flex: 1.2,
    },
    "&:last-child": {
      flex: 0.7,
    },
  },
  plCardOverview: {
    flex: 1,
    marginBottom: 0,
    marginTop: 0,
    borderRadius: "6px",
    "&:first-child": {
      flex: 1.5,
    },
    "&:last-child": {
      flex: 0.8,
    },
  },
  plCardBody: {
    padding: "0 !important",
    minWidth: 0,
    overflow: "hidden",
  },
  highlightsRightColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },

  // P&L comparison styles
  plComparison: {
    display: "flex",
    gap: "0",
  },
  plColumn: {
    flex: 1,
    padding: "8px",
    "&:not(:last-child)": {
      borderRight: "1px solid " + grayColor[8],
    },
  },
  plColumnHeader: {
    fontSize: "12px",
    fontWeight: "500",
    color: grayColor[4],
    textAlign: "center",
    marginBottom: "8px",
    padding: "2px 5px",
    borderRadius: "5px 5px 0 0",
  },
  plLine: {
    display: "flex",
    justifyContent: "space-between",
    padding: "4px 4px",
    fontSize: "12px",
    color: grayColor[6],
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  plLineName: {
    color: grayColor[2],
  },
  plLineValue: {
    fontWeight: "400",
    color: grayColor[1],
    textAlign: "right",
  },
  plLineHighlight: {
    fontWeight: "500",
  },
  plLineLevel1: {
    fontWeight: "500",
  },
  plLineLevel2: {
    paddingLeft: "8px",
  },
  plLineLevel3: {
    paddingLeft: "16px",
    fontStyle: "italic",
    fontSize: "11px",
  },

  // Waterfall card styles
  waterfallCard: {
    marginBottom: "10px",
    marginTop: 0,
    flex: "0 0 auto",
  },
  waterfallCardBody: {
    padding: "12px",
  },
  waterfallTitle: {
    fontSize: "14px",
    color: "#999",
    textAlign: "center",
  },
  waterfallChartWrapper: {
    height: "200px",
    marginTop: "8px",
  },
  waterfallPlaceholder: {
    height: "180px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px dashed " + grayColor[6],
    borderRadius: "6px",
    color: grayColor[5],
    fontSize: "14px",
  },

  // Drivers card styles
  driversCard: {
    marginBottom: 0,
    marginTop: 0,
    flex: 1,
    "&:not(:last-child)": {
      marginBottom: "10px",
    },
  },
  driversSection: {
    padding: " 8px 0",
  },
  driversSectionHeader: {
    display: "flex",
    alignItems: "center",
    paddingBottom: "12px",
    borderBottom: "1px solid #eee",
    marginBottom: "12px",
  },
  driversSectionIcon: {
    fontSize: "24px",
    marginRight: "8px",
  },
  driversSectionTitle: {
    fontSize: "16px",
    fontWeight: "500",
    margin: 0,
  },
  driverRow: {
    display: "flex",
    alignItems: "center",
    padding: "6px 10px 6px 8px",
    marginBottom: "3px",
    borderRadius: "0 4px 4px 0",
    backgroundColor: "#fff",
    borderLeft: "3px solid",
    transition: "all 0.2s",
  },
  driverArrow: {
    marginRight: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
  },
  driverArrowIcon: {
    fontSize: "14px",
    transition: "transform 0.3s",
  },
  driverNarrative: {
    flex: 1,
    fontSize: "13px",
    color: "#333",
    lineHeight: "1.4",
  },
  driverElement: {
    fontWeight: "600",
    textDecoration: "underline",
    textDecorationStyle: "dotted",
    cursor: "pointer",
  },

  // Section title styles
  sectionTitleCardHeader: {
    color: "rgb(120, 120, 120)",
    backgroundColor: "rgba(238, 238, 238, 0.267)",
    fontSize: "14px",
    fontWeight: "400",
    padding: "2px 8px",
    borderRadius: "6px 6px 0 0",
    textTransform: "uppercase",
    textAlign: "center",
  },
  // Variant with icon for drivers headers
  sectionTitleCardHeaderWithIcon: {
    color: "rgb(120, 120, 120)",
    backgroundColor: "rgba(238, 238, 238, 0.267)",
    fontSize: "14px",
    fontWeight: "400",
    padding: "4px 12px",
    borderRadius: "6px 6px 0 0",
    textTransform: "uppercase",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    "& svg": {
      fontSize: "18px",
    },
  },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: "12px",
    color: grayColor[2],
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  // View tabs styles
  viewTabs: {
    display: "flex",
    gap: "8px",
    marginBottom: "16px",
    flexWrap: "wrap",
  },
  viewTab: {
    padding: "3px 14px",
    borderRadius: "16px",
    border: "1px solid " + grayColor[5],
    backgroundColor: whiteColor,
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "400",
    color: grayColor[2],
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: grayColor[4],
      backgroundColor: grayColor[10],
    },
  },
  viewTabActive: {
    fontWeight: "500",
    color: whiteColor,
    borderColor: "transparent",
  },
  pnlContainer: {
    flex: 1,
    overflow: "auto",
  },

  // Charts section styles
  chartsStack: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    flex: 1,
    minHeight: 0,
    height: "100%",
  },
  chartBox: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 0",
    minHeight: 0,
    maxHeight: "calc(33.33% - 3px)", // Exactly 1/3 minus gap
    overflow: "hidden",
  },
  chartTitle: {
    fontSize: "12px",
    fontWeight: "400",
    color: "rgb(120, 120, 120)",
    backgroundColor: "rgba(238, 238, 238, 0.6)",
    textAlign: "center",
    marginTop: "6px",
    flexShrink: 0,
  },
  chartWrapper: {
    flex: 1,
    minHeight: 0,
    height: "calc(100% - 20px)",
  },
  heatmapWrapper: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 0,
  },
  chartPlaceholder: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: grayColor[10],
    borderRadius: "4px",
    color: grayColor[6],
    fontSize: "12px",
  },

  // Heatmap table styles - synchronized with bar chart axes (fontSize: 10, color: grayColor[2])
  heatmapTable: {
    width: "100%",
    fontSize: "10px",
    borderCollapse: "separate",
    borderSpacing: "2px",
    marginTop: "-15px",
  },
  heatmapHeader: {
    display: "grid",
    gridTemplateColumns: "24px repeat(12, 1fr)",
    gap: "0",
    marginBottom: "1px",
  },
  heatmapRow: {
    display: "grid",
    gridTemplateColumns: "24px repeat(12, 1fr)",
    gap: "0",
    marginBottom: "1px",
  },
  heatmapYearHeader: {
    fontSize: "10px",
    fontWeight: "400",
    color: grayColor[0],
    textAlign: "center",
    padding: "2px 0",
  },
  heatmapMonthHeader: {
    fontSize: "10px",
    fontWeight: "400",
    color: grayColor[0],
    textAlign: "center",
    padding: "0",
  },
  heatmapYearLabel: {
    fontSize: "10px",
    fontWeight: "400",
    color: grayColor[0],
    textAlign: "center",
  },
  heatmapMonthLabel: {
    fontSize: "10px",
    fontWeight: "400",
    color: grayColor[0],
    textAlign: "center",
    padding: "2px 0",
  },
  heatmapYearCell: {
    fontSize: "10px",
    fontWeight: "600",
    color: grayColor[0],
    textAlign: "center",
    padding: "4px 4px 4px 0",
  },
  heatmapCell: {
    fontSize: "9px",
    fontWeight: "500",
    textAlign: "center",
    padding: "3px 1px",
    borderRadius: "3px",
    color: grayColor[1],
    "&.hm1": { backgroundColor: "#F44336", color: "#fff" },
    "&.hm2": { backgroundColor: "#F76C24", color: "#fff" },
    "&.hm3": { backgroundColor: "#FB9612", color: "#fff" },
    "&.hm4": { backgroundColor: "#FFC000", color: grayColor[1] },
    "&.hm5": { backgroundColor: "#F6C737", color: grayColor[1] },
    "&.hm6": { backgroundColor: "#EECE6E", color: grayColor[1] },
    "&.hm7": { backgroundColor: "#E5D5A5", color: grayColor[1] },
    "&.hm8": { backgroundColor: "#DDDDDD", color: grayColor[1] },
    "&.hm9": { backgroundColor: "#C8D6C8", color: grayColor[1] },
    "&.hm10": { backgroundColor: "#B3CFB4", color: grayColor[1] },
    "&.hm11": { backgroundColor: "#9EC9A0", color: grayColor[1] },
    "&.hm12": { backgroundColor: "#8AC28C", color: grayColor[1] },
    "&.hm13": { backgroundColor: "#75BC78", color: "#fff" },
    "&.hm14": { backgroundColor: "#60B564", color: "#fff" },
    "&.hm15": { backgroundColor: "#4CAF50", color: "#fff" },
  },
  heatmapNoData: {
    backgroundColor: grayColor[10],
    color: grayColor[5],
  },
  heatmapPositive: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
  },
  heatmapNegative: {
    backgroundColor: "#ffebee",
    color: "#c62828",
  },
  heatmapNeutral: {
    backgroundColor: whiteColor,
    color: grayColor[2],
  },

  // Messages section styles
  messagesContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    overflow: "visible",
    margin: "0",
    "&:first-of-type": {
      marginTop: "16px",
    },
    // When hover in a messageCard, apply fade to all messages
    "&:has($messageCard:hover) $messageCard": {
      opacity: 0.5,
    },
    // A mensagem que recebe o hover mantém opacidade total
    "&:has($messageCard:hover) $messageCard:hover": {
      opacity: 1,
    },
  },
  messageCard: {
    padding: "12px",
    paddingRight: "32px", // Space for edit button
    borderLeft: "3px solid",
    backgroundColor: grayColor[13],
    // borderRadius: "0 4px 4px 0",
    position: "relative",
    cursor: "pointer",
    transition:
      "transform 0.3s ease, box-shadow 0.3s ease, opacity 0.3s ease, margin 0.3s ease, border-radius 0.3s ease, z-index 0s",
    transformOrigin: "left center",
    "&:hover": {
      transform: "scale(1.2)",
      zIndex: 100,
      boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
      borderRadius: "0 8px 8px 0",
      marginTop: "5px",
      marginBottom: "5px",
    },
  },
  messageCardLoading: {
    pointerEvents: "none",
    "&:hover": {
      transform: "none !important",
      zIndex: "auto !important",
      boxShadow: "none !important",
      borderRadius: "0 !important",
      marginTop: "0 !important",
      marginBottom: "0 !important",
    },
  },
  messageEditButton: {
    position: "absolute",
    top: "8px",
    right: "4px",
    padding: "4px",
    color: grayColor[2],
    opacity: 0.6,
    transition: "opacity 0.2s",
    "&:hover": {
      opacity: 1,
      backgroundColor: "transparent",
    },
  },
  messageTitle: {
    fontSize: "16px",
    fontWeight: "500",
    color: grayColor[1],
    marginBottom: "12px",
  },
  messageText: {
    fontSize: "14px",
    lineHeight: "1.5",
    color: grayColor[2],
    "& *": {
      backgroundColor: "transparent !important",
    },
    "& p": {
      margin: 0,
    },
    "& b, & strong": {
      fontWeight: "600",
    },
    "& i, & em": {
      fontStyle: "italic",
    },
    "& u": {
      textDecoration: "underline",
    },
  },
  messageAddButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "fit-content",
    alignSelf: "center",
    padding: "8px 16px",
    fontSize: "13px",
    color: grayColor[3],
    border: `1px dashed ${grayColor[7]}`,
    borderRadius: "4px",
    backgroundColor: "transparent",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: grayColor[12],
      borderColor: grayColor[4],
      color: grayColor[1],
    },
  },

  // ActionBar styles
  actionWrapperOuter: {
    paddingLeft: "10px !important",
    paddingRight: "10px !important",
  },
  actionWrapper: {
    cursor: "pointer",
    opacity: "0.8",
    transition: "opacity 0.1s",
    "&:hover,&:active": {
      opacity: "1",
      "& div:first-child": {
        boxShadow: "0 0px 30px -10px rgba(" + hexToRgb(blackColor) + ", 0.3)",
      },
      "& div:first-child > svg": {
        transform: "scale(1.2)",
      },
    },
    "&:active": {
      "& div:first-child": {
        boxShadow:
          "0 0px 30px -10px rgba(" + hexToRgb(blackColor) + ", 0.7) !important",
      },
    },
  },
  actionIcon: {
    color: grayColor[6],
    margin: "6px auto 0",
    width: "42px",
    height: "42px",
    border: "1px solid " + grayColor[11],
    borderRadius: "50%",
    textAlign: "center",
    lineHeight: "58px",
    transition: "box-shadow 0.2s",
    "& svg": {
      marginLeft: "1px",
      transition: "transform 0.2s",
      width: "26px",
      height: "26px",
    },
  },
  actionText: {
    color: grayColor[0],
    marginTop: "6px",
    marginBottom: "2px",
    textAlign: "center",
    fontSize: "10px",
  },

  // Top Clients Section styles
  topClientsContainer: {
    marginTop: "30px",
  },
  topClientsContainerFocus: {
    marginTop: "30px",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  topClientsMainLayout: {
    display: "flex",
    gap: "10px",
    height: "calc(100vh - 176px)",
  },
  topClientsMainLayoutFocus: {
    display: "flex",
    gap: "10px",
    flex: 1,
    maxHeight: "100%",
    overflow: "hidden",
  },
  topClientsLeftCard: {
    flex: "0 0 35%",
    display: "flex",
    flexDirection: "column",
    maxHeight: "100%",
    "& > div": {
      // Card
      flex: 1,
      display: "flex",
      flexDirection: "column",
      marginBottom: 0,
      marginTop: 0,
      maxHeight: "100%",
    },
    "& > div > div": {
      // CardBody
      flex: 1,
      display: "flex",
      flexDirection: "column",
      padding: "12px 6px",
      minHeight: 0,
      maxHeight: "100%",
      overflow: "hidden",
    },
  },
  topClientsRightCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    maxHeight: "100%",
    "& > div": {
      // Card
      flex: 1,
      display: "flex",
      flexDirection: "column",
      marginBottom: 0,
      marginTop: 0,
      maxHeight: "100%",
    },
    "& > div > div": {
      // CardBody
      flex: 1,
      display: "flex",
      flexDirection: "column",
      padding: "16px",
      overflow: "hidden",
    },
  },
  // Right column with two stacked cards (when no client selected)
  topClientsRightColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    maxHeight: "100%",
  },
  // Pareto card (top) - ~60% of space
  topClientsParetoCard: {
    flex: "0 0 calc(55% + 40px)",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    "& > div": {
      // Card
      flex: 1,
      display: "flex",
      flexDirection: "column",
      marginBottom: 0,
      marginTop: 0,
      minHeight: 0,
    },
    // CardBody - select last child div to skip sectionTitleCardHeader
    "& > div > div:last-child": {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      padding: "4px 0 8px",
      overflow: "hidden",
    },
  },
  // Cascade card (bottom) - remaining space
  topClientsCascadeCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    "& > div": {
      // Card
      flex: 1,
      display: "flex",
      flexDirection: "column",
      marginBottom: 0,
      marginTop: 0,
      minHeight: 0,
    },
    // CardBody - select last child div to skip sectionTitleCardHeader
    "& > div > div:last-child": {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      padding: "12px",
      overflow: "hidden",
    },
  },
  clientsTable: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    marginLeft: "-6px",
    marginRight: "-6px",
  },
  clientsTableHeader: {
    display: "grid",
    gridTemplateColumns: "36px 3.4fr repeat(6, 1.1fr)",
    gap: "2px",
    paddingBottom: "8px",
    borderBottom: "1px solid " + grayColor[8],
    flexShrink: 0,
    backgroundColor: whiteColor,
    alignItems: "flex-end",
  },
  clientsTableHeaderCell: {
    fontSize: "12px",
    fontWeight: "500",
    color: grayColor[2],
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "2px",
    "&:first-child": {
      textAlign: "center",
      justifyContent: "center",
    },
    "&:nth-child(2)": {
      textAlign: "left",
      alignItems: "flex-start",
      justifyContent: "flex-end",
    },
  },
  dimensionDropdownCell: {
    position: "relative",
  },
  dimensionDropdownButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: "4px",
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: grayColor[10],
    },
  },
  dimensionDropdownIcon: {
    fontSize: "18px",
    transition: "transform 0.2s ease",
  },
  dimensionDropdownIconOpen: {
    transform: "rotate(180deg)",
  },
  dimensionDropdownMenu: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: "4px",
    backgroundColor: whiteColor,
    borderRadius: "6px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
    zIndex: 100,
    minWidth: "150px",
    overflow: "hidden",
  },
  dimensionDropdownItem: {
    padding: "10px 14px",
    fontSize: "13px",
    color: grayColor[2],
    cursor: "pointer",
    transition: "background-color 0.1s ease",
    "&:hover": {
      backgroundColor: grayColor[10],
    },
  },
  dimensionDropdownItemActive: {
    fontWeight: "500",
    backgroundColor: grayColor[10],
  },
  clientsTableBody: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    paddingTop: "8px",
    // Overlay scrollbar - doesn't take space
    // Using overlay for webkit browsers (scrollbar floats over content)
    "@supports (overflow: overlay)": {
      overflowY: "overlay",
    },
    scrollbarWidth: "thin",
    scrollbarColor: "transparent transparent",
    "&:hover": {
      scrollbarColor: grayColor[6] + " transparent",
    },
    "&::-webkit-scrollbar": {
      width: "6px",
      position: "absolute",
      right: 0,
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "transparent",
      borderRadius: "3px",
    },
    "&:hover::-webkit-scrollbar-thumb": {
      background: grayColor[6],
    },
  },
  clientsTableRow: {
    display: "grid",
    gridTemplateColumns: "36px 3.4fr repeat(6, 1.1fr)",
    gap: "2px",
    padding: "6px 4px",
    margin: "0 -4px",
    borderBottom: "1px solid " + grayColor[9],
    cursor: "pointer",
    transition: "opacity 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.03)",
    },
    "&:last-child": {
      borderBottom: "none",
    },
  },
  clientsTableRowFaded: {
    opacity: 0.4,
  },
  clientsTableRowSelected: {
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    fontWeight: "500",
  },
  clientsTableCell: {
    fontSize: "14px",
    color: grayColor[2],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 0,
    "&:first-child": {
      justifyContent: "center",
    },
    "&:nth-child(2)": {
      justifyContent: "flex-start",
      overflow: "hidden",
    },
  },
  clientRankingCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "2px",
    fontSize: "13px",
    fontWeight: "500",
    color: grayColor[3],
  },
  rankingChangeIcon: {
    fontSize: "14px",
    marginLeft: "1px",
  },
  rankingUp: {
    color: successColor[0],
  },
  rankingDown: {
    color: dangerColor[0],
  },
  rankingNeutral: {
    color: grayColor[5],
  },
  clientElementName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    display: "block",
  },
  clientNameWithGroup: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
  },
  clientGroupBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "16px",
    height: "16px",
    minWidth: "16px",
    borderRadius: "3px",
    color: "#fff",
    fontWeight: 600,
    fontSize: "10px",
    flexShrink: 0,
  },
  clientValueWithBadge: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "1px",
    lineHeight: 1.2,
  },
  clientBadge: {
    width: "20px",
    height: "3px",
    borderRadius: "2px",
    "&.n1": { backgroundColor: "#b71c1c" },
    "&.n2": { backgroundColor: "#c62828" },
    "&.n3": { backgroundColor: "#d32f2f" },
    "&.n4": { backgroundColor: "#e53935" },
    "&.n5": { backgroundColor: "#ff5722" },
    "&.n6": { backgroundColor: "#ff9800" },
    "&.n7": { backgroundColor: "#ffc107" },
    "&.n8": { backgroundColor: grayColor[6] },
    "&.n9": { backgroundColor: "#cddc39" },
    "&.n10": { backgroundColor: "#8bc34a" },
    "&.n11": { backgroundColor: "#4caf50" },
    "&.n12": { backgroundColor: "#43a047" },
    "&.n13": { backgroundColor: "#388e3c" },
    "&.n14": { backgroundColor: "#2e7d32" },
    "&.n15": { backgroundColor: "#1b5e20" },
  },
  clientDetailPlaceholder: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: grayColor[2],
    fontSize: "14px",
    textAlign: "center",
    padding: "20px",
  },
  // New 4-card layout for selected client
  clientDetailLayout: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    maxHeight: "100%",
    minWidth: 0,
    overflow: "hidden",
  },
  // Card 1: Header with client name and share
  clientHeaderCard: {
    flex: "0 0 auto",
    "& > div": {
      marginBottom: 0,
      marginTop: 0,
    },
    "& > div > div": {
      padding: "10px 16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
  },
  clientHeaderContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  clientHeaderTitle: {
    fontSize: "15px",
    fontWeight: "600",
    color: grayColor[2],
  },
  clientHeaderShare: {
    fontSize: "13px",
    color: grayColor[4],
  },
  // Bottom row with donut cards and P&L card
  clientDetailBottomRow: {
    flex: 1,
    display: "flex",
    gap: "10px",
    minHeight: 0,
    minWidth: 0,
    overflow: "hidden",
  },
  // Left column with 2 donut cards stacked
  clientDonutColumn: {
    flex: "0 0 35%",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    minHeight: 0,
  },
  // Each donut card
  clientDonutCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    "& > div": {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      marginBottom: 0,
      marginTop: 0,
      minHeight: 0,
    },
    "& > div > div": {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      padding: "8px 4px",
      minHeight: 0,
    },
  },
  // P&L card (right side)
  clientPnlCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    minWidth: 0,
    overflow: "hidden",
    "& > div": {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      marginBottom: 0,
      marginTop: 0,
      minHeight: 0,
      minWidth: 0,
    },
    "& > div > div": {
      flex: 1,
      display: "flex",
      minWidth: 0,
      flexDirection: "column",
      padding: "8px 12px",
      minHeight: 0,
      overflow: "hidden",
    },
  },
  clientPnlTitle: {
    fontSize: "13px",
    fontWeight: 500,
    color: grayColor[2],
    marginBottom: "6px",
    flexShrink: 0,
  },
  clientPnlWrapper: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    width: "100%",
    overflow: "hidden",
  },
  donutChartContainer: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  clientOverviewContent: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  paretoChartSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
  },
  paretoChartTitle: {
    fontSize: "14px",
    fontWeight: "500",
    color: grayColor[2],
    marginBottom: "4px",
    flexShrink: 0,
  },
  paretoChartWrapper: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  overviewBottomSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    paddingTop: "12px",
  },
  // Cascade tree section (now in its own card)
  cascadeTreeSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
  },
  cascadeTreeTitle: {
    fontSize: "14px",
    fontWeight: "500",
    color: grayColor[2],
    marginBottom: "-25px",
    flexShrink: 0,
  },
  cascadeTreeWrapper: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
  },
};

export default alignmentSessionStyles;
